import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { get } from "lodash";
import { toast } from "sonner";

import { queryClient } from "App";
import { addResizedColumns } from "hooks/useTableConfig";
import { getCampaignActivityTableColumns } from "routes/constant_contact/components/campaign_activity_table/CampaignActivityTableColumnsAndFilters";
import { CAMPAIGN_ACTIVITY_TABLE_CONSTANTS } from "routes/constant_contact/components/campaign_activity_table/CampaignActivityTableV3";
import ConstantContactCampaignsService from "services/ConstantContactCampaigns";
import UserService from "services/User";

export const invalidateCampaignActivitiesQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [CAMPAIGN_ACTIVITY_TABLE_CONSTANTS.queryKey],
  });
};

export const useCampaignActivityMutations = ({ onDeleted } = {}) => {
  const create = useMutation({
    mutationFn: data =>
      ConstantContactCampaignsService.createCampaignActivity(data),
    onSuccess: () => {
      invalidateCampaignActivitiesQuery();
      toast.success("Campaign activity created");
    },
  });

  const update = useMutation({
    mutationFn: ({ id, data }) =>
      ConstantContactCampaignsService.editCampaignActivity(id, data),
    onSuccess: () => {
      invalidateCampaignActivitiesQuery();
      toast.success("Campaign activity updated");
    },
  });

  const remove = useMutation({
    mutationFn: id =>
      ConstantContactCampaignsService.deleteCampaignActivity(id),
    onSuccess: () => {
      invalidateCampaignActivitiesQuery();
      toast.success("Campaign activity deleted");
      onDeleted?.();
    },
    onError: () => toast.error("Failed to delete campaign activity"),
  });

  return { create, update, remove };
};

export const useCampaignActivityTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getCampaignActivityTableColumns();
    const campaignActivityTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${CAMPAIGN_ACTIVITY_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: campaignActivityTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return { columns };
};
